/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { INotesConfig, IDocumentsConfig, IAssessmentsConfig, ITasksConfig, IContactLogConfig } from 'app/core/models/essette-interfaces';
import { openGlobalModal, IModalData, IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { AuthService } from 'app/um/services/auth-service';
import { apiCall } from 'app/shared/lib/api/api';
import { AuthPortalPageContext } from 'app/pp/lib/auth-portal-page-context';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { AuthContext } from 'app/um/lib/auth-context';
import { CWQIService } from 'app/mcg/services/cwqi-service';
import { guid } from 'app/shared/lib/guid';
import { NoteSavedEvent } from 'app/core/components/notes/notes-grid';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { IAuthMedicationsGridOptions } from 'app/um/components/auth/medications/auth-medications-grid';
import { IAuthInformationOptions } from 'app/um/components/summary/auth-information';
import { IServiceGridOptions } from 'app/um/components/auth/service-grid';
import { MemberType } from 'app/cd/services/member-service';
import * as UM from 'app/um/models/essette-api';
import * as MCG from 'app/mcg/models/essette-api';
import { deepClone } from 'app/shared/lib/utilities';
import { requiredIncludes as cwqiEpisodeGuidelinesRequiredIncludes, requiredFields as cwqiEpisodeGuidelinesRequiredFields } from 'app/ext/cwqi/components/cwqi-episode-guideline-grid';

export interface IAuthSummaryModelData {
    auth: UM.Auth;
    readonly?: boolean;
}

@autoinject()
export class AuthSummary extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected auth: UM.Auth | null = null;
    protected cwqiEpisodeGuidelines: MCG.CwqiEpisodeGuideline[] = [];

    protected readonly: boolean = false;
    protected canUploadToClosedAuth: boolean = false;

    protected isProviderPortal: boolean = false;
    protected memberType: MemberType = MemberType.Member;

    protected chiefComplaint: string | null = null;

    protected notesConfig: INotesConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        moduleCode: 'UM',
        addTargetProperty: this.addAuthProperty.bind(this),
        flagCodes: ['UM.Summary']
    };

    protected assessmentsConfig: IAssessmentsConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        moduleCode: 'UM'
    };

    protected taskConfig: ITasksConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth'
    };

    protected documentsConfig: IDocumentsConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        addTargetProperty: (document: UM.AuthDocument) => { document.auth = { id: this.auth?.id } }
    };

    protected contactLogConfig: IContactLogConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth'
    };

    protected serviceGridOptions: IServiceGridOptions | null = null;
    protected dentalServiceGridOptions: IServiceGridOptions | null = null;
    protected authMedicationsGridOptions: IAuthMedicationsGridOptions | null = null;
    protected authInformationOptions: IAuthInformationOptions | null = null;
    protected hasPortalAssessment: boolean = false;

    protected context: AuthContext | null = null;
    protected headerLevelTat: boolean = true;

    protected id: guid = guid();

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly cwqiService: CWQIService,
        private readonly portalContext: AuthPortalPageContext,
        private readonly authContext: AuthPageContext,
        ...rest: any[]) {

        super(...rest);
    }

    protected async activate(model: IAuthSummaryModelData): Promise<void> {
        this.isProviderPortal = this.env.application.code === 'Portal.Providers';
        if(this.isProviderPortal) {
            this.documentsConfig.categoryFlagCodes = ['PP.View'];
            this.memberType = MemberType.Patient;
        }

        this.serviceGridOptions = {
            canOpenServiceStatusHistory: !this.isProviderPortal,
            canSyncToClaimsSystems: !this.isProviderPortal,
            showDueDate: !this.isProviderPortal,
            showServiceStatusReason: this.isProviderPortal,
            showQuantityApproved: true,
            canApplyBulkServiceStatus: !this.isProviderPortal,
            canApplyCorrespondenceRules: !this.isProviderPortal,
            showExternalActions: !this.isProviderPortal,
            showApprovedWithRequestedCharges: this.isProviderPortal,
            canSyncToClaimsSystemsLineLevel: !this.isProviderPortal,
            showExistingAuthsWarning: !this.isProviderPortal,
            showDuplicateAuths: false,
            showAddDocumentButton: false,
            showOpenIqcSummary: true,
            showWarnings: false,
            showModifiers: false
        };

        this.dentalServiceGridOptions = {
            canOpenServiceStatusHistory: !this.isProviderPortal,
            canSyncToClaimsSystems: !this.isProviderPortal,
            showDueDate: !this.isProviderPortal,
            showServiceStatusReason: this.isProviderPortal,
            showQuantityApproved: true,
            canApplyBulkServiceStatus: !this.isProviderPortal,
            canApplyCorrespondenceRules: !this.isProviderPortal,
            showExternalActions: !this.isProviderPortal,
            showApprovedWithRequestedCharges: this.isProviderPortal,
            canSyncToClaimsSystemsLineLevel: !this.isProviderPortal,
            showExistingAuthsWarning: !this.isProviderPortal,
            showDuplicateAuths: false,
            showAddDocumentButton: false,
            showOpenIqcSummary: true,
            showWarnings: false,
            showModifiers: false
        };

        this.authMedicationsGridOptions = {
            showStatusReason: this.isProviderPortal,
            showQtyApproved: true,
            showDaysApproved: !this.isProviderPortal,
            showApprovedWithRequestedCharges: this.isProviderPortal,
            showWarnings: false
        };

        this.authInformationOptions = {
            showCWQIAction: !this.isProviderPortal,
            showIQCAction: this.isProviderPortal,
            showAssignedTo: !this.isProviderPortal,
            showDueDate: !this.isProviderPortal,
            showHealthcareServiceType: false,
            showPlaceOfService: false
        };

        if(!model) return;

        this.readonly = model.readonly ?? this.readonly;

        if(!model.auth?.id) return;

        this.auth = model.auth;
        this.loadGuidelines();
        /** Initialize action includes adding any assessments required by the auth's sub-class (if they don't exist) */
        if(!this.isProviderPortal) await this.initializeAuth(model.auth.id);

        this.chiefComplaint = model.auth.chiefComplaint?.description || null;
        if(!this.chiefComplaint) {
            this.loadChiefComplaint(model.auth.id);
        }
        if(!this.auth.preferredContact) {
            this.loadPreferredContact();
        }
        this.context = this.isProviderPortal ? this.portalContext : this.authContext;
        await this.context.configure(model.auth?.id);
        this.checkPermissions();
        this.serviceGridOptions.showModifiers = this.context?.settings?.servicesModifier?.visible ?? false;
        this.dentalServiceGridOptions.showModifiers = this.context?.settings?.servicesDentalModifier?.visible ?? false;
        this.authInformationOptions.showHealthcareServiceType = this.context?.settings?.healthcareServiceType?.visible ?? false;
        this.authInformationOptions.showPlaceOfService = this.context?.settings?.placeOfService?.visible ?? false;
        let portalAssessments = this.context.auth.assessments ?? [];
        if(portalAssessments.find(assessment => !!assessment.attachment?.createdInPortal) != null) {
            this.hasPortalAssessment = true;
        }
        // if model data does not specify member and member id is not currently known, load auth to get member id
        if(!model.auth?.member?.id && !this.auth.member?.id) {
            const fields: string[] = ['member.*'];
            const returnedAuth = await this.loadAuth(model.auth.id, fields);
            this.auth.member = returnedAuth.member;
        }
    }


    ////****************** Events ***************************************************////
    protected async handleNoteSaved(event: NoteSavedEvent) {
        if(!this.isProviderPortal || !this.auth) return;
        await this.authContext.loadBasicSettings();
        if(this.authContext.noteAttachmentSetAuthStatus) this.authContext.setAuthStatus(this.auth?.id, 'PP.Auth.Note.Attached');
        if(this.authContext.noteAttachmentRouteToQueue) this.authContext.triggerQueueRoutingRules(this.auth?.id, 'PP.Auth.Note.Attached');
    }

    protected async handleDocumentSaved(event: FormSuccessCustomEvent) {
        if(!this.isProviderPortal || !this.auth) return;
        await this.authContext.loadBasicSettings();
        if(this.authContext.documentAttachmentSetAuthStatus) this.authContext.setAuthStatus(this.auth?.id, 'PP.Auth.Document.Attached');
        if(this.authContext.documentAttachmentRouteToQueue) this.authContext.triggerQueueRoutingRules(this.auth?.id, 'PP.Auth.Document.Attached');
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        const canEditAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditAuth') || this.isProviderPortal;
        const canEditClosedAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditClosedAuth');
        this.canUploadToClosedAuth = this.isProviderPortal || this.accountContext.hasPermission('UM.AuthDocument.Duty.EditUpdateClosedAuthDocuments');

        this.readonly = !!this.context?.authIsReadOnly(canEditAuth, canEditClosedAuth);
        this.headerLevelTat = !this.authContext.lineLevelTAT;
    }

    private addAuthProperty(note: UM.AuthNote): void {
        note.auth = { id: this.auth?.id };
    }

    protected reloadGuidelines(): void {
        if(this.auth?.id) this.loadGuidelines();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadAuth(authId: string, fields: string[] = []): Promise<UM.Auth> {
        try {
            this.state.beginLoad();

            const response = await this.service.loadAuth(authId, fields);
            return response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadChiefComplaint(authId: string): Promise<void> {
        if(!authId) { return; }
        try {
            this.state.beginLoad();

            const response = await this.service.loadChiefComplaint(authId, ['description']);
            this.chiefComplaint = response.results[0]?.description || null;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadPreferredContact(): Promise<void> {
        if(!this.auth?.id) return;
        try {
            this.state.beginLoad();

            const response = await this.service.loadAuth(this.auth.id, ['preferredContact']);
            this.auth.preferredContact = response.results[0]?.preferredContact ? deepClone(response.results[0].preferredContact) : null;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadGuidelines(): Promise<void> {
        if(this.isProviderPortal || !this.auth?.id || !this.env.config.integrations.includes('cwqi')) return;

        try {
            this.state.beginLoad();
            const includes: string[] = cwqiEpisodeGuidelinesRequiredIncludes;
            const fields: string[] = cwqiEpisodeGuidelinesRequiredFields;
            const responseGuidelines = await this.cwqiService.loadCwqiEpisodeGuidelines(this.auth.id, undefined, includes, fields, true);
            this.cwqiEpisodeGuidelines = responseGuidelines.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async initializeAuth(authId: string): Promise<void> {
        try {
            this.state.beginSave();
            await this.service.initializeAuth(authId);
        } catch(error) {
            this.logger.error('Error calling initialize action on auth');
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}

/**
 * Opens the auth summary modal with the global dialog
 * @param auth The auth information to use
 */
export function openAuthSummary(auth: UM.Auth, hasAuthRoute: boolean = false, readonly: boolean = false, moduleCode: string = 'um'): void {
    if(!auth || !auth.id) {
        throw new Error('Auth is missing or invalid');
    }

    const dialogOptions: IDialogOptions = {
        width: '10',
        height: '11',
        helpTopic: `${moduleCode}.auth.summary`,
        title: {
            key: 'um:routes.auth.summary.$title',
            replace: { number: auth.number }
        },
        print: true,
        popout: {
            route: `${moduleCode}.auth.summary`,
            params: { authId: auth.id }
        }
    };

    const modelData: IAuthSummaryModelData = {
        auth: auth,
        readonly: readonly
    };

    const modalData: IModalData = {
        componentId: 'app/um/components/summary/auth-summary',
        modelData: modelData,
        dialogOptions: hasAuthRoute ? {
            ...dialogOptions,
            openRecord: {
                route: `${moduleCode}.auth`,
                params: {
                    authId: auth.id,
                },
                tooltipDesc: {
                    key: 'ui.actions.goTo',
                    replace: {
                        what: `$t(${moduleCode}:auth.$)`,
                        item: auth.number
                    }
                }
            }
        }
            :
            dialogOptions
    };

    openGlobalModal(modalData);
}
